import React, { useState } from 'react';
// Framer Motion supprimé - utilisation d'animations CSS
import { 
  Search, 
  Filter, 
  Download, 
  Plus, 
  Eye, 
  Edit, 
  Trash2,
  FileText,
  Calendar,
  User,
  MapPin,
  Users,
  TrendingUp,
  Clock
} from 'lucide-react';
import { useStudies } from '../../hooks/useApi';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const StudiesList = () => {
  const { data: studies = [], loading, error } = useStudies();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const statuses = [...new Set(studies.map(study => study.status))];

  const filteredStudies = studies.filter(study => {
    const matchesSearch = study.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         study.researcher.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         study.zone.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = !filterStatus || study.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Titre
    doc.setFontSize(20);
    doc.text('Répertoire des Études Médicales', 20, 20);
    
    // Tableau
    const tableData = filteredStudies.map(study => [
      study.title,
      study.researcher,
      study.zone,
      study.startDate,
      study.endDate,
      study.status,
      study.participants.toString()
    ]);

    autoTable(doc, {
      head: [['Titre', 'Chercheur', 'Zone', 'Début', 'Fin', 'Statut', 'Participants']],
      body: tableData,
      startY: 30,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [245, 158, 11] }
    });

    doc.save('repertoire-etudes.pdf');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'En cours':
        return 'bg-blue-100 text-blue-800';
      case 'Terminée':
        return 'bg-green-100 text-green-800';
      case 'Suspendue':
        return 'bg-yellow-100 text-yellow-800';
      case 'Annulée':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'En cours':
        return <Clock className="h-4 w-4" />;
      case 'Terminée':
        return <TrendingUp className="h-4 w-4" />;
      case 'Suspendue':
        return <Clock className="h-4 w-4" />;
      case 'Annulée':
        return <FileText className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getProgressPercentage = (startDate, endDate, status) => {
    if (status === 'Terminée') return 100;
    if (status === 'Annulée') return 0;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();
    
    const total = end.getTime() - start.getTime();
    const elapsed = now.getTime() - start.getTime();
    
    return Math.min(Math.max((elapsed / total) * 100, 0), 100);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className=" px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div
          
          
          className="mb-8"
        >
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gestion des Études Médicales</h1>
              <p className="text-gray-600 mt-2">Suivez et gérez les études de recherche médicale</p>
            </div>
            <div className="flex space-x-3">
              <button
                
                
                onClick={exportToPDF}
                className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
              >
                <Download className="h-4 w-4" />
                <span>Exporter PDF</span>
              </button>
              <button
                
                
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
              >
                <Plus className="h-4 w-4" />
                <span>Nouvelle Étude</span>
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div
            
            
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8 text-center"
          >
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Chargement des études...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div
            
            
            className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erreur de chargement</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filter */}
        {!loading && !error && (
        <div
          
          
          
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Rechercher une étude..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Tous les statuts</option>
                {statuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <div className="text-sm text-gray-500 flex items-center">
              {filteredStudies.length} étude(s) trouvée(s)
            </div>
          </div>
        </div>
        )}

        {/* Studies Grid */}
        {!loading && !error && (
        <div
          
          
          
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredStudies.map((study, index) => {
            const progress = getProgressPercentage(study.startDate, study.endDate, study.status);
            
            return (
              <div
                key={study.id}
                
                
                
                
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <FileText className="h-6 w-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">{study.title}</h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(study.status)}`}>
                        {getStatusIcon(study.status)}
                        <span className="ml-1">{study.status}</span>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <User className="h-4 w-4 mr-2" />
                    <span>{study.researcher}</span>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="h-4 w-4 mr-2" />
                    <span>{study.zone}</span>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-600">
                    <Users className="h-4 w-4 mr-2" />
                    <span>{study.participants} participants</span>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span>{formatDate(study.startDate)} - {formatDate(study.endDate)}</span>
                  </div>
                </div>

                {/* Progress Bar */}
                {study.status === 'En cours' && (
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Progrès</span>
                      <span>{Math.round(progress)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        
                        animate={{ width: `${progress}%` }}
                        
                        className="bg-orange-500 h-2 rounded-full"
                      />
                    </div>
                  </div>
                )}

                <div className="flex space-x-2">
                  <button
                    
                    
                    className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-2 px-4 rounded-lg flex items-center justify-center space-x-2 transition-colors duration-200"
                  >
                    <Eye className="h-4 w-4" />
                    <span>Voir</span>
                  </button>
                  <button
                    
                    
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg flex items-center justify-center space-x-2 transition-colors duration-200"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Modifier</span>
                  </button>
                  <button
                    
                    
                    className="bg-red-100 hover:bg-red-200 text-red-700 py-2 px-4 rounded-lg transition-colors duration-200"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        )}

        {filteredStudies.length === 0 && (
          <div
            
            
            className="text-center py-12"
          >
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune étude trouvée</h3>
            <p className="text-gray-500">Essayez de modifier vos critères de recherche</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudiesList;
